<script setup lang="ts">
import { ref, computed, watch, nextTick, onMounted } from 'vue';
import type { CSSProperties, StyleValue } from 'vue';
import ConnxTransition from '../ConnxTransition.vue';
import AvailableIcons from '@/interfaces/AvailableIcons';
import { ButtonStyles } from './ConnxButton.vue';
import { buttonStyleCssClassMap } from '@/lib/button-styles';
import ConnxIcon from '../ConnxIcon.vue';
import { ButtonAction } from '../ConnxButton.vue';
import { useSlots } from 'vue';
import { useElementBounding, useWindowSize } from '@vueuse/core';

const slots = useSlots();

const hasDefaultSlot = computed(() => {
  if (!slots.default) return false;

  const slotContent = slots.default();
  return slotContent.some((vnode) => {
    if (Array.isArray(vnode.children) && vnode.children.length > 0) {
      return true;
    }
    if (vnode.component || vnode.el) {
      return true;
    }
    return false;
  });
});

interface Props {
  actions?: ButtonAction[];
  disabled?: boolean;
  icon?: AvailableIcons | null;
  iconPosition?: 'before' | 'after' | null;
  buttonStyle?: ButtonStyles;
  dropdownClass?: string;
  dropdownContainerClass?: string;
  dropdownPosition?: 'top' | 'bottom' | 'bottom-left';
}

const props = withDefaults(defineProps<Props>(), {
  type: 'button',
  link: null,
  icon: null,
  iconPosition: 'before',
  buttonStyle: 'secondary',
  dropdownPosition: 'top',
});

const emits = defineEmits(['click', 'open']);

const buttonRef = ref();
const dropdownEl = ref();
const optionsOpen = ref(false);
const dropdownWidth = ref(0);

// Get window size
const { width: windowWidth } = useWindowSize();

// Track dropdown element position
const dropdownRect = useElementBounding(dropdownEl);

// Compute if we should align to the right to avoid going off-screen
const shouldAlignLeft = ref(false);

const computeDropdownPosition = () => {
  if (!dropdownRect.right.value || !windowWidth.value) return false;
  shouldAlignLeft.value = dropdownRect.right.value > windowWidth.value;
};

const setDropdownWidth = async () => {
  const interval = setInterval(async () => {
    await nextTick();
    if (dropdownRect.width.value > 0) {
      dropdownWidth.value = dropdownRect.width.value;
      clearInterval(interval);
    }
  }, 250);
};

onMounted(async () => {
  await nextTick();
  setDropdownWidth();
  computeDropdownPosition();
});

const dropdownStyle = computed<StyleValue>(() => {
  if (dropdownWidth.value === 0) {
    return { visibility: 'hidden' };
  }
  if (props.dropdownPosition.includes('left') || shouldAlignLeft.value) {
    return { left: `-${dropdownWidth.value}px` };
  } else {
    return { left: '0px' };
  }
});

// Main button classes
const classList = computed(() => {
  const styleClasses = buttonStyleCssClassMap.get(props.buttonStyle) || [];
  const classes = [...styleClasses, 'Connx-btn-dropdown__main'];

  // Add icon positioning
  if (props.icon && props.iconPosition === 'before') {
    classes.push('Connx-btn--icon-before');
  } else if (props.icon) {
    classes.push('Connx-btn--icon-after');
  } else {
    classes.push('Connx-btn--no-icon');
  }

  // Add disabled state
  if (props.disabled) {
    classes.push('Connx-btn--disabled');
  }

  return classes;
});

// Dropdown button classes
const classListDropdown = computed(() => {
  const styleClasses = buttonStyleCssClassMap.get(props.buttonStyle) || [];
  const classes = [...styleClasses, hasDefaultSlot.value ? 'Connx-btn-dropdown__toggle' : null];

  // Add disabled state
  if (props.disabled) {
    classes.push('Connx-btn--disabled');
  }

  // Add custom dropdown class
  if (props.dropdownClass) {
    classes.push(props.dropdownClass);
  }

  return classes;
});

const carryOutButtonAction = (action: () => void) => {
  optionsOpen.value = false;
  action();
};

const emitClick = () => {
  buttonRef.value?.blur();
  emits('click');
};

const toggleOptions = () => {
  optionsOpen.value = !optionsOpen.value;
  if (optionsOpen.value) {
    nextTick(() => {
      dropdownRect.update();
    });
  }
};

watch(optionsOpen, async (newValue) => {
  emits('open', newValue);
  await nextTick();
  computeDropdownPosition();
});

// Watch for window resize to recalculate position
watch(windowWidth, () => {
  if (optionsOpen.value) {
    nextTick(() => {
      dropdownRect.update();
    });
  }
});
</script>

<template>
  <div class="Connx-btn-dropdown" v-click-outside="() => (optionsOpen = false)">
    <button
      ref="buttonRef"
      v-if="hasDefaultSlot"
      :disabled="disabled"
      type="button"
      aria-label="Save"
      :class="classList"
      @click="emitClick()"
    >
      <connx-icon
        v-if="icon && iconPosition === 'before'"
        :name="icon"
        class="Connx-btn__icon Connx-btn__icon--before"
      />
      <span class="Connx-btn-dropdown__content">
        <slot></slot>
      </span>
      <connx-icon v-if="icon && iconPosition === 'after'" :name="icon" class="Connx-btn__icon Connx-btn__icon--after" />
    </button>
    <div class="Connx-btn-dropdown__wrapper">
      <connx-transition>
        <ul
          ref="dropdownEl"
          v-show="optionsOpen || dropdownWidth === 0"
          :class="[
            dropdownContainerClass,
            {
              'Connx-btn-dropdown__menu--top': dropdownPosition.includes('top'),
              'Connx-btn-dropdown__menu--bottom': dropdownPosition.includes('bottom'),
            },
          ]"
          :style="dropdownStyle"
          class="Connx-btn-dropdown__menu"
        >
          <li
            v-for="(buttonAction, index) in actions"
            :key="buttonAction.text"
            class="Connx-btn-dropdown__item"
            :class="{
              'Connx-btn-dropdown__item--bordered': index !== 0,
            }"
          >
            <button
              :disabled="disabled"
              type="button"
              @click="carryOutButtonAction(buttonAction.action)"
              class="Connx-btn-dropdown__action"
              :class="buttonAction.class"
              icon-position="before"
            >
              <div v-if="buttonAction.icon" class="Connx-btn-dropdown__action-icon">
                <connx-icon :name="buttonAction.icon"></connx-icon>
              </div>
              <div class="Connx-btn-dropdown__action-text">{{ buttonAction.text }}</div>
            </button>
          </li>
        </ul>
      </connx-transition>
      <button :disabled="disabled" type="button" :class="classListDropdown" @click="toggleOptions()">
        <slot name="drop-down-icon">
          <connx-icon name="fal fa-chevron-down" class="Connx-btn-dropdown__chevron"></connx-icon>
        </slot>
      </button>
    </div>
  </div>
</template>
